import { OnlineMenuItem } from "@dining/api-client";

export const defaultValue = "/.default";

// This won't currently work as an enum.
// "Nearest" moves to the end of the list until we re-enable it.
export const sortOption = ["A to Z", "Z to A", "Nearest"];

export const appInsightsEvents = {
  System: {
    AppInitialized: "System.AppInitialized",
    BackendCall: "System.BackendCall",
  },
  Usage: {
    LoggedIn: "Usage.LoggedIn",
    ViewComponent: "Usage.ViewComponent",
    Actions: {
      SortBuildings: "Usage.Actions.SortBuildings",
      CafeSelected: "Usage.Actions.CafeSelected",
      CalendarDaySelected: "Usage.Actions.CalendarDaySelected",
      StationSelected: "Usage.Actions.StationSelected",
      MenuItemSelected: "Usage.Actions.MenuItemSelected",
      CafeChanged: "Usage.Actions.CafeChanged",
      NavigateToCheckout: "Usage.Actions.NavigateToCheckout",
      NavigateBack: "Usage.Actions.NavigateBack",
      NavigateToPrivacyStatement: "Usage.Actions.NavigateToPrivacyStatement",
      SelectPaymentOption: "Usage.Actions.SelectPaymentOption",
      AbandonCart: "Usage.Actions.AbandonCart",
      OrderSelected: "Usage.Actions.OrderSelected",
      AddItemToCart: "Usage.Actions.AddItemToCart",
      ItemEditInCart: "Usage.Actions.ItemEditInCart",
      HoursDialogDismissed: "Usage.Actions.HoursDialogDismissed",
      AbandonCartDialogConfirmed: "Usage.Actions.AbandonCartDialogConfirmed",
      AbandonCartDialogDismissed: "Usage.Actions.AbandonCartDialogDismissed",
      RemoveItemDialogConfirmed: "Usage.Actions.RemoveItemDialogConfirmed",
      RemoveItemDialogDismissed: "Usage.Actions.RemoveItemDialogDismissed",
      OrderPlaced: "Usage.Actions.OrderPlaced",
      OrderPickedUp: "Usage.Actions.OrderPickedUp",
      OrderResolved: "Usage.Actions.OrderResolved",
      Feedback: {
        Rendered: "Usage.Actions.Feedback.Rendered",
        Clicked: "Usage.Actions.Feedback.Clicked",
        LaunchSuccess: "Usage.Actions.Feedback.LaunchSuccess",
        LaunchFailure: "Usage.Actions.Feedback.LaunchFailure",
        Submitted: "Usage.Actions.Feedback.Submitted",
        Dismissed: "Usage.Actions.Feedback.Dismissed",
      },
      WebChat: {
        Opened: "Usage.Actions.WebChat.Opened",
        Closed: "Usage.Actions.WebChat.Closed",
        MessageSent: "Usage.Actions.WebChat.MessageSent",
        NewChat: "Usage.Actions.WebChat.NewChat",
        Retry: "Usage.Actions.WebChat.Retry",
        MicPermissionRequested: "Usage.Actions.WebChat.MicPermissionRequested",
        MicPermissionDenied: "Usage.Actions.WebChat.MicPermissionDenied",
        RecordingStarted: "Usage.Actions.WebChat.RecordingStarted",
        RecordingStopped: "Usage.Actions.WebChat.RecordingStopped",
        TranscriptionRequested: "Usage.Actions.WebChat.TranscriptionRequested",
        TranscriptionSucceeded: "Usage.Actions.WebChat.TranscriptionSucceeded",
        TranscriptionFailed: "Usage.Actions.WebChat.TranscriptionFailed",
        // Conversation outcome — fired from `useWebChatTelemetry`.
        // `Resolved`  : bot signalled `endOfConversation` event activity.
        // `Abandoned` : user closed the panel after sending at least one
        //               message, but without an `endOfConversation` signal.
        Resolved: "Usage.Actions.WebChat.Resolved",
        Abandoned: "Usage.Actions.WebChat.Abandoned",
      },
      WebAgent: {
        Opened: "Usage.Actions.WebAgent.Opened",
        Closed: "Usage.Actions.WebAgent.Closed",
        MessageSent: "Usage.Actions.WebAgent.MessageSent",
        NewChat: "Usage.Actions.WebAgent.NewChat",
        Retry: "Usage.Actions.WebAgent.Retry",
        Resolved: "Usage.Actions.WebAgent.Resolved",
        Abandoned: "Usage.Actions.WebAgent.Abandoned",
      },
      AbandonCartDialog: {
        CafeChangeConfirmed: "Usage.Actions.AbandonCartDialog.CafeChangeConfirmed",
        CafeChangeDismissed: "Usage.Actions.AbandonCartDialog.CafeChangeDismissed",
        DatePickerChangeConfirmed: "Usage.Actions.AbandonCartDialog.DatePickerChangeConfirmed",
        DatePickerChangeDismissed: "Usage.Actions.AbandonCartDialog.DatePickerChangeDismissed",
        ItemOrderConfirmed: "Usage.Actions.AbandonCartDialog.ItemOrderConfirmed",
        ItemOrderDismissed: "Usage.Actions.AbandonCartDialog.ItemOrderDismissed",
        AddItemToCartConfirmed: "Usage.Actions.AbandonCartDialog.AddItemToCartConfirmed",
        AddItemToCartDismissed: "Usage.Actions.AbandonCartDialog.AddItemToCartDismissed",
        HomeBreadcrumbConfirmed: "Usage.Actions.AbandonCartDialog.HomeBreadcrumbConfirmed",
        HomeBreadcrumbDismissed: "Usage.Actions.AbandonCartDialog.HomeBreadcrumbDismissed",
      },
      AddToFavoriteCafesDialog: {
        CafeAddConfirmed: "Usage.Actions.AddToFavoriteCafesDialog.CafeAddConfirmed",
        CafeAddDismissed: "Usage.Actions.AddToFavoriteCafesDialog.CafeAddDismissed",
        CafeRemoveConfirmed: "Usage.Actions.AddToFavoriteCafesDialog.CafeRemoveConfirmed",
        CafeRemoveDismissed: "Usage.Actions.AddToFavoriteCafesDialog.CafeRemoveDismissed",
      },
      Payment: {
        PaymentFailedDialog: "Usage.Actions.PaymentFailedDialog",
        ApplePay: {
          FeatureAccessCheck: "Usage.Actions.Payment.ApplePay.FeatureAccessCheck",
          PaymentFailure: "System.PaymentFailure",
          PaymentSuccess: "System.PaymentSuccess",
          MerchantSessionRequestSuccess: "System.MerchantSessionRequestSuccess",
          MerchantSessionRequestFailure: "System.MerchantSessionRequestFailure",
        },
      },
      NavigateToOrderHistory: "Usage.Actions.NavigateToOrderHistory",
      NavigateToOrderSummary: "Usage.Actions.NavigateToOrderSummary",
    },
  },
  Performance: {
    WebChat: {
      // Time in milliseconds between WEB_CHAT/SEND_MESSAGE and the first bot
      // `message` activity in response. Emitted via App Insights trackMetric
      // so percentile aggregations (p50/p95/p99) work natively in Kusto.
      ResponseLatencyMs: "Performance.WebChat.ResponseLatencyMs",
      SpeechToTextLatencyMs: "Performance.WebChat.SpeechToTextLatencyMs",
    },
    WebAgent: {
      ResponseLatencyMs: "Performance.WebAgent.ResponseLatencyMs",
    },
  },
};

export type MenuCategory = {
  id: string;
  name: string;
  items: OnlineMenuItem[];
};

export const categoryOthers: string = "Others";

export const abandonCartDialogContent =
  "You currently have an active cart. Doing so will remove your items. Do you wish to continue?";
export const abandonCartDialogTitle = "Clear your cart?";
export const clearActiveCart = "Clear active cart?";
export const removeDefaultCafe = "Remove your default cafe?";
export const setDefaultCafe = "Set as your default cafe?";
export const addToFavoritesContent =
  "You can always change or remove your default cafe and switch back to finding your nearest location.";
export const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
export const MAX_BREADCRUMB_TEXT_LENGTH = 30;
export const MAX_ACTIVE_MESSAGES_FETCH_COUNT = "5";

export const FEATURE_FLAG_NAMES = {
  diningAgent: "diningAgent",
  voiceInput: "voiceInput",
} as const;
